import Add20 from '@carbon/icons-react/es/add/20';
import Time16 from '@carbon/icons-react/es/time/16';
import { Upload } from 'antd';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { FileAttachment } from 'api/attachments';
import { AttachmentType } from 'api/attachments/types';
import { Loading } from 'components/atoms/loading';
import { confirm } from 'components/atoms/modal';
import { toastService } from 'components/molecules/toast';
import { RoutePaths } from 'constant';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { queryClient } from 'queries';
import { useAttachmentVersionMutation } from 'queries/attachments';
import {
  getProjectFileGroupsQueryKey,
  useLinkFilesToProjectsMutation,
} from 'queries/project-files';
import { extname } from 'utils/files';
import { AnnotationVersionList } from './annotation-version-list';
import { fileFormatSupported } from './constants';

const VersionIconWrapper = styled.span`
  color: var(--gray-50);
`;

const StyledText = styled.span`
  font-size: 14px;
  color: #000000;
  padding-left: 5px;
  position: relative;
  bottom: 3px;
`;

const AddNewVersion = styled.div`
  padding-left: var(--spacing-05);
  padding-right: var(--spacing-06);
  position: sticky;
  top: 0px;
  background-color: var(--coolest-gray-10);
  z-index: 10;
`;

const HeaderWrapper = styled.div`
  padding: var(--spacing-04) 0;
`;

const VersionListWrapper = styled.div<{ isOpen: boolean }>`
  height: 100%;
  background: var(--coolest-gray-10);
  overflow-y: auto;
  scroll-behavior: smooth;
  padding-bottom: ${({ isOpen }) => (isOpen ? 'var(--spacing-05)' : '0')};
  min-width: ${({ isOpen }) => (isOpen ? '180px' : '0px')};
  width: ${({ isOpen }) => (isOpen ? '180px' : '0px')};
  overflow-x: hidden;
  transition: all 0.5s cubic-bezier(0, 0, 0.3, 1);
  position: relative;
  .ant-upload {
    display: block;
  }
`;

const NewVersionWrapper = styled.div`
  width: 100%;
  height: 83px;
  border: 1px solid var(--gray-40);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-grow: 1;
  margin: var(--spacing-03) 0;
  color: var(--gray-40);
  cursor: pointer;

  &:hover {
    border: 1px solid var(--blue-60);
    color: var(--blue-60);
  }
`;

const UploadWrapper = styled(Upload)`
  width: 100%;
`;

const NewVersionText = styled.div`
  color: inherit;
  font-size: var(--spacing-04);
`;

const AddIcon = styled(Add20)`
  color: inherit;
`;

interface Props {
  isVersionSectionVisible: boolean;
  latestVersion: FileAttachment;
  versionFileList: FileAttachment[];
  setVersionFileList: (attachment: FileAttachment[]) => void;
  selectedVersion: FileAttachment;
  setSelectedVersion: (attachment: FileAttachment) => void;
  setLatestVersion: (attachment: FileAttachment) => void;
  setLeftAttachment: (attachment: FileAttachment) => void;
  setRightAttachment: (attachment: FileAttachment) => void;
}

export const VersionList: FC<Props> = ({
  isVersionSectionVisible,
  latestVersion,
  versionFileList,
  setVersionFileList,
  selectedVersion,
  setSelectedVersion,
  setLatestVersion,
  setLeftAttachment,
  setRightAttachment,
}) => {
  const translate = useTranslate();
  const history = useHistory();

  const { mutateAsync: createAttchmentVersion, isLoading } =
    useAttachmentVersionMutation(latestVersion.attachmentId, {
      projectId: latestVersion.projectId,
      sourceId: latestVersion.sourceId,
      sourceType: latestVersion.sourceType,
    });
  const { mutateAsync: linkFilesToProjects } = useLinkFilesToProjectsMutation(
    ['project_id', latestVersion.projectId],
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          getProjectFileGroupsQueryKey(['project_id', latestVersion.projectId]),
        );
      },
    },
  );

  const message = useTranslation('status_updates.attachments.error.title');
  const description = useTranslation(
    'status_updates.attachments.error.description',
  );
  const toast = toastService({
    title: message,
    message: description,
  });

  const postUploadFiles = useCallback(
    async (groupId: number, fileIds: number[]) => {
      await linkFilesToProjects({
        groupId,
        fileIds,
      });

      queryClient.invalidateQueries(
        getProjectFileGroupsQueryKey(['project_id', latestVersion.projectId]),
      );
    },
    [latestVersion.projectId, linkFilesToProjects],
  );

  const onUploadRequest = useCallback(
    (file: File) => {
      return createAttchmentVersion(
        { file },
        {
          onError: () => {
            toast.error();
          },
        },
      );
    },
    [createAttchmentVersion, toast],
  );

  const customUploadRequest = (args) => {
    const { file } = args;

    const fileExtension = extname(file?.name) ?? '';

    if (!fileFormatSupported.includes(fileExtension.toLowerCase())) {
      confirm({
        okText: translate('ok'),
        title: translate('image_preview.file.not_supported.title'),
        content: (
          <>
            <div>{translate('image_preview.file.not_supported.reason')}</div>
            <br />
            <div>{translate('image_preview.file.not_supported.content')}</div>
            <ul>
              {translate('image_preview.file.supported.types', {
                li: (chunks) => <li>{chunks}</li>,
              })}
            </ul>
          </>
        ),
        onOk: () => {},
      });
    } else {
      onUploadRequest(file)
        .then((res) => {
          setVersionFileList([res, ...versionFileList]);
          setSelectedVersion(res);
          setLatestVersion(res);
          if (
            latestVersion.sourceType === AttachmentType.Folder &&
            latestVersion.sourceId
          ) {
            postUploadFiles(latestVersion.sourceId, [res.attachmentId]);
          }

          history.replace(
            RoutePaths.attachmentWorkspace.replace(
              ':attachmentId',
              res.attachmentId.toString(),
            ),
          );
        })
        .catch(({ response = {} }) => {
          const errorData = response?.data || {};
          const [error = {}] = errorData.errors || [];

          if (error.code === 'ATTACHMENT_SIZE_EXCEEDED') {
            toastService({
              title: translate('attachment.error.size.title'),
              message: translate('attachment.add.description'),
            }).error();
          }
        });
    }
  };

  return (
    <VersionListWrapper isOpen={isVersionSectionVisible}>
      <AddNewVersion>
        <HeaderWrapper>
          <VersionIconWrapper>
            <Time16 />
          </VersionIconWrapper>
          <StyledText>
            <FormattedMessage id="image_preview.version.history" />
          </StyledText>

          <UploadWrapper
            customRequest={customUploadRequest}
            showUploadList={false}
          >
            <NewVersionWrapper>
              {isLoading ? <Loading size="small" /> : <AddIcon />}
              <NewVersionText>
                <FormattedMessage id="image_preview.new.version" />
              </NewVersionText>
            </NewVersionWrapper>
          </UploadWrapper>
        </HeaderWrapper>
      </AddNewVersion>
      <AnnotationVersionList
        latestVersion={latestVersion}
        versionFileList={versionFileList || []}
        setVersionFileList={(attachmentList) =>
          setVersionFileList(attachmentList)
        }
        selectedVersion={selectedVersion}
        setSelectedVersion={(attachment) => setSelectedVersion(attachment)}
        setLatestVersion={(attachment) => setLatestVersion(attachment)}
        setLeftAttachment={(attachment) => setLeftAttachment(attachment)}
        setRightAttachment={(attachment) => setRightAttachment(attachment)}
      />
    </VersionListWrapper>
  );
};
